import React, {
  useState,
  useEffect,
  useRef,
  useMemo,
  type MutableRefObject,
} from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import jsQR from 'jsqr';
import { BrowserQRCodeReader } from '@zxing/browser';
import { IoMdArrowBack } from 'react-icons/io';
import { IoRefreshOutline } from 'react-icons/io5';

import { addVPA } from '../../redux/vpaActions';
import { fetchTenant } from '../../redux/tenantActions';
import { fetchProgramWithId } from '../../redux/programActions';
import { showErrorToast } from '../../utils/toast';
import type { AppDispatch } from '../../redux/store';
import { useCollectionMode } from '../../hooks/useCollectionMode';
import { normalizeWalletAddress } from '../../utils/collectionMode';

type RootState = any;

interface UpiDetails {
  vpa: string | null;
  providerName: string | null;
  merchantName: string | null;
}

const QRScanner: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const returnToApp = (location.state as { returnTo?: string } | null)?.returnTo === 'app';
  const dispatch = useDispatch<AppDispatch>();
  const { isWalletAddressCollection } = useCollectionMode();
  const { tenant_details, loading: tenantLoading } = useSelector((state: RootState) => state.tenant);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const controlsRef: MutableRefObject<any | null> = useRef(null);
  const isCapturingRef = useRef(false);
  const [isVideoReady, setIsVideoReady] = useState(false);
  const [isOpenCVReady, setIsOpenCVReady] = useState(false);
  const [cameraError, setCameraError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  const [image, setImage] = useState<string | null>(null);
  const [qrData, setQrData] = useState<string | null>(null);
  const [isScanning, setIsScanning] = useState(true);
  const croppedCanvasRef = useRef<HTMLCanvasElement | null>(null);

  const stopScanner = () => {
    if (controlsRef.current) {
      controlsRef.current.stop();
      controlsRef.current = null;
    }
    const stream = videoRef.current?.srcObject as MediaStream | null;
    if (stream) {
      stream.getTracks().forEach((track) => track.stop());
      if (videoRef.current) {
        videoRef.current.srcObject = null;
      }
    }
    setIsVideoReady(false);
  };

  useEffect(() => {
    if (id) {
      dispatch(fetchTenant(id));
    }
  }, [dispatch, id]);

  useEffect(() => {
    const pid = tenant_details?.programId;
    if (pid != null) {
      dispatch(fetchProgramWithId(pid));
    }
  }, [dispatch, tenant_details?.programId]);

  useEffect(() => {
    const checkOpenCV = () => {
      if (window.cv && window.isOpenCVReady) {
        setIsOpenCVReady(true);
        return true;
      }
      return false;
    };

    if (!checkOpenCV()) {
      const interval = setInterval(() => {
        if (checkOpenCV()) {
          clearInterval(interval);
        }
      }, 100);

      return () => clearInterval(interval);
    }

    return undefined;
  }, []);

  useEffect(() => {
    if (isScanning) {
      isCapturingRef.current = false;
      const timeoutId = setTimeout(() => {
        void startScanning();
      }, 100);

      return () => {
        clearTimeout(timeoutId);
        stopScanner();
      };
    }

    return undefined;
  }, [isScanning]);

  const startScanning = async () => {
    try {
      if (controlsRef.current) {
        return;
      }
      if (!videoRef.current) {
        setTimeout(() => {
          void startScanning();
        }, 100);
        return;
      }

      setCameraError(null);
      const codeReader = new BrowserQRCodeReader();
      controlsRef.current = await codeReader.decodeFromVideoDevice(
        undefined,
        videoRef.current,
        (result) => {
          if (result && !isCapturingRef.current) {
            isCapturingRef.current = true;
            // eslint-disable-next-line no-console
            console.log('QR Code detected:', result.getText());
            void captureImage(result.getText());
          }
        },
      );
      setIsVideoReady(true);
    } catch (err) {
      setIsVideoReady(false);
      const error = err as { name?: string; message?: string };
      if (error?.name === 'NotAllowedError') {
        setCameraError('Camera permission was denied. Please allow camera access and refresh.');
        showErrorToast('Camera permission denied. Please allow access and retry.');
      } else if (error?.name === 'NotFoundError') {
        setCameraError('No camera device found on this device.');
        showErrorToast('No camera device found.');
      } else {
        setCameraError('Unable to start camera. Please try again.');
        showErrorToast('Unable to start camera. Please try again.');
      }
      isCapturingRef.current = false;
      // eslint-disable-next-line no-console
      console.error('Failed to start scanning:', err);
    }
  };

  const captureImage = async (resultText: string) => {
    const videoElement = videoRef.current;
    if (!videoElement) {
      // eslint-disable-next-line no-console
      console.error('Video element not found');
      showErrorToast('Camera is not ready yet. Please try again.');
      return;
    }

    try {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        isCapturingRef.current = false;
        return;
      }

      canvas.width = videoElement.videoWidth;
      canvas.height = videoElement.videoHeight;
      ctx.drawImage(videoElement, 0, 0);
      const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);

      const qrCode = jsQR(imageData.data, canvas.width, canvas.height);
      setQrData(resultText);
      if (qrCode && isOpenCVReady && window.cv) {
        const {
          topLeftCorner: tl,
          topRightCorner: tr,
          bottomRightCorner: br,
          bottomLeftCorner: bl,
        } = qrCode.location;

        const qrWidth = Math.max(
          Math.hypot(tr.x - tl.x, tr.y - tl.y),
          Math.hypot(br.x - bl.x, br.y - bl.y),
        );
        const qrHeight = Math.max(
          Math.hypot(bl.x - tl.x, bl.y - tl.y),
          Math.hypot(br.x - tr.x, br.y - tr.y),
        );

        const padding = 10;
        const outputCanvas = document.createElement('canvas');
        outputCanvas.width = qrWidth + padding * 2;
        outputCanvas.height = qrHeight + padding * 2;

        warpImage(
          canvas,
          outputCanvas,
          [tl, tr, br, bl],
          [
            { x: padding, y: padding },
            { x: qrWidth + padding, y: padding },
            { x: qrWidth + padding, y: qrHeight + padding },
            { x: padding, y: qrHeight + padding },
          ],
        );

        croppedCanvasRef.current = outputCanvas;
        setImage(outputCanvas.toDataURL('image/png'));
      } else {
        // Fallback: keep a full-frame snapshot if OpenCV crop is unavailable.
        croppedCanvasRef.current = canvas;
        setImage(canvas.toDataURL('image/png'));
      }
      setIsScanning(false);
      stopScanner();
    } catch (error) {
      isCapturingRef.current = false;
      // eslint-disable-next-line no-console
      console.error('Error capturing image:', error);
    }
  };

  const warpImage = (
    srcCanvas: HTMLCanvasElement,
    dstCanvas: HTMLCanvasElement,
    srcCorners: Array<{ x: number; y: number }>,
    dstCorners: Array<{ x: number; y: number }>,
  ) => {
    try {
      const cv = window.cv as any;
      const src = cv.imread(srcCanvas);
      const dst = new cv.Mat();
      const srcTri = cv.matFromArray(4, 1, cv.CV_32FC2, [
        srcCorners[0].x, srcCorners[0].y,
        srcCorners[1].x, srcCorners[1].y,
        srcCorners[2].x, srcCorners[2].y,
        srcCorners[3].x, srcCorners[3].y,
      ]);
      const dstTri = cv.matFromArray(4, 1, cv.CV_32FC2, [
        dstCorners[0].x, dstCorners[0].y,
        dstCorners[1].x, dstCorners[1].y,
        dstCorners[2].x, dstCorners[2].y,
        dstCorners[3].x, dstCorners[3].y,
      ]);

      const M = cv.getPerspectiveTransform(srcTri, dstTri);
      const dsize = new cv.Size(dstCanvas.width, dstCanvas.height);
      cv.warpPerspective(src, dst, M, dsize, cv.INTER_LINEAR, cv.BORDER_CONSTANT, new cv.Scalar());
      cv.imshow(dstCanvas, dst);

      src.delete();
      dst.delete();
      srcTri.delete();
      dstTri.delete();
      M.delete();
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error in warpImage:', error);
    }
  };

  const handleReset = () => {
    stopScanner();
    isCapturingRef.current = false;
    setCameraError(null);
    setIsScanning(true);
    setQrData(null);
    setImage(null);
    croppedCanvasRef.current = null;
  };

  const parseEmvcoTlv = (str: string): Record<string, string> => {
    const result: Record<string, string> = {};
    let i = 0;

    while (i + 4 <= str.length) {
      const tag = str.substring(i, i + 2);
      const length = Number.parseInt(str.substring(i + 2, i + 4), 10);
      if (!Number.isFinite(length) || length < 0) {
        break;
      }
      const valueStart = i + 4;
      const valueEnd = valueStart + length;
      if (valueEnd > str.length) {
        break;
      }
      const value = str.substring(valueStart, valueEnd);
      result[tag] = value;
      i = valueEnd;
    }

    return result;
  };

  const extractUpiDetailsFromTlv = (
    rawString: string,
  ): UpiDetails & { mcc?: string | null } => {
    const tlvData = parseEmvcoTlv(rawString);
    const merchantField = tlvData['26'];

    let vpa: string | null = null;
    let merchantName: string | null = tlvData['59'] || null;
    let providerName: string | null = null;

    const mcc: string | null = tlvData['52'] || null;

    if (merchantField) {
      const nested = parseEmvcoTlv(merchantField);
      vpa = nested['01'] || null;
      merchantName = nested['02'] || merchantName;
      providerName = vpa ? vpa.split('@')[1] : null;
    }

    return { vpa, providerName, merchantName, mcc };
  };

  const extractUpiDetails = (
    upiLink: string | null,
  ): UpiDetails & { mcc?: string | null } => {
    if (!upiLink) {
      return {
        vpa: null,
        providerName: null,
        merchantName: null,
        mcc: null,
      };
    }

    if (upiLink.startsWith('upi://')) {
      try {
        const url = new URL(upiLink);
        const vpa = url.searchParams.get('pa');
        const merchantName = url.searchParams.get('pn') || null;
        const mcc = url.searchParams.get('mc') || null;
        const providerName = vpa ? vpa.split('@')[1] : null;
        return { vpa, providerName, merchantName, mcc };
      } catch {
        return { vpa: null, providerName: null, merchantName: null, mcc: null };
      }
    }
    return extractUpiDetailsFromTlv(upiLink);
  };

  const saveUrlAndQr = async (
    canvas: HTMLCanvasElement | null,
    body: {
      vpa: string | null;
      providerName: string | null;
      merchantName: string | null;
      status: string;
      qrString?: string | null;
      mcc?: string | null;
    },
    collectionMode: 'vpa' | 'wallet',
  ) => {
    if (!canvas || !id || !body.vpa) {
      console.error('Missing required QR save data');
      return;
    }

    const vpaValue = body.vpa;

    canvas.toBlob(async (blob) => {
      if (!blob) return;

      await dispatch(
        addVPA(
          blob,
          vpaValue,
          body.providerName,
          body.status,
          body.merchantName,
          id,
          body.qrString ?? null,
          body.mcc ?? null,
          collectionMode,
        ),
      );
      navigate(returnToApp ? `/app/shops/${id}` : `/tenant/vpa/${id}`);
    }, 'image/png');
  };

  const handleScan = async () => {
    try {
      setIsSaving(true);
      if (isWalletAddressCollection) {
        const addr = normalizeWalletAddress(qrData ?? '');
        if (!addr) {
          showErrorToast('No valid wallet address detected in this QR.');
          return;
        }
        await saveUrlAndQr(
          croppedCanvasRef.current,
          {
            vpa: addr,
            providerName: 'BLOCKCHAIN',
            merchantName: null,
            status: 'ACTIVE',
            qrString: qrData ?? null,
            mcc: null,
          },
          'wallet',
        );
        return;
      }
      const result = extractUpiDetails(qrData);
      if (!result.vpa) {
        showErrorToast('No valid VPA detected in this QR.');
        return;
      }
      await saveUrlAndQr(
        croppedCanvasRef.current,
        {
          vpa: result.vpa,
          providerName: result.providerName,
          merchantName: result.merchantName,
          status: 'ACTIVE',
          qrString: qrData ?? null,
          mcc: result.mcc ?? null,
        },
        'vpa',
      );
    } finally {
      setIsSaving(false);
    }
  };

  const scannedDetails = useMemo(() => {
    if (isWalletAddressCollection) {
      const addr = normalizeWalletAddress(qrData ?? '');
      return {
        vpa: addr,
        providerName: addr ? 'BLOCKCHAIN' : null,
        merchantName: null as string | null,
        mcc: null as string | null,
      };
    }
    return extractUpiDetails(qrData);
  }, [qrData, isWalletAddressCollection]);
  const statusText = useMemo(() => {
    if (cameraError) return cameraError;
    if (!isVideoReady) return 'Initializing camera...';
    if (!isOpenCVReady) return 'Camera ready. Optimizing scan...';
    return 'Camera is ready. Point at a QR code.';
  }, [cameraError, isOpenCVReady, isVideoReady]);

  if (tenantLoading && !tenant_details) {
    return (
      <div className="min-h-screen bg-surface flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-brand border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!tenantLoading && !tenant_details) {
    return (
      <div className="min-h-screen bg-surface flex flex-col items-center justify-center">
        <p className="text-gray-500">Shop not found</p>
        <button
          type="button"
          onClick={() => navigate(returnToApp ? '/app/shops' : '/principal')}
          className="mt-4 text-brand"
        >
          Go back
        </button>
      </div>
    );
  }

  return (
    <div className="h-screen bg-white overflow-auto">
      <div className="bg-white border-b border-gray-100 shadow-soft sticky top-0 z-30">
        <div className="max-w-[1200px] mx-auto">
          <div className="px-4 sm:px-6">
            <div className="h-16 flex items-center">
              <button
                type="button"
                onClick={() => navigate(returnToApp ? `/app/shops/${id}` : `/tenant/vpa/${id}`)}
                className="p-2.5 hover:bg-gray-100 active:bg-gray-200 rounded-xl transition-colors"
              >
                <IoMdArrowBack className="w-5 h-5 text-gray-600" />
              </button>
              <div className="flex-1 flex justify-center">
                <div className="text-center">
                  <h1 className="text-lg font-semibold text-gray-900">
                    {isWalletAddressCollection ? 'Scan wallet QR' : 'Scan QR'}
                  </h1>
                  <div className="text-xs sm:text-sm text-gray-600 space-y-1">
                    <div>{tenant_details.tenantName}</div>
                    <div className={cameraError ? 'text-red-600 font-medium' : ''}>{statusText}</div>
                  </div>
                </div>
              </div>
              <button
                type="button"
                onClick={handleReset}
                className="p-2.5 hover:bg-gray-100 active:bg-gray-200 rounded-xl transition-colors"
              >
                <IoRefreshOutline className="w-5 h-5 text-gray-600" />
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="flex-1 bg-gray-50/80">
        <div className="max-w-5xl mx-auto p-4 sm:p-6">
          <div className="space-y-6">
            <div className="bg-white rounded-2xl shadow-soft overflow-hidden border border-gray-100 relative">
              {isScanning && (
                <>
                  <video
                    ref={videoRef}
                    className="w-full bg-black"
                    style={{ minHeight: '340px' }}
                    playsInline
                    autoPlay
                    muted
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/45 via-black/10 to-black/25 pointer-events-none" />
                  <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                    <div className="relative w-60 h-60 rounded-2xl border border-white/70 shadow-[0_0_0_9999px_rgba(0,0,0,0.18)]">
                      <div className="absolute -top-0.5 -left-0.5 w-8 h-8 border-t-4 border-l-4 border-white rounded-tl-xl" />
                      <div className="absolute -top-0.5 -right-0.5 w-8 h-8 border-t-4 border-r-4 border-white rounded-tr-xl" />
                      <div className="absolute -bottom-0.5 -left-0.5 w-8 h-8 border-b-4 border-l-4 border-white rounded-bl-xl" />
                      <div className="absolute -bottom-0.5 -right-0.5 w-8 h-8 border-b-4 border-r-4 border-white rounded-br-xl" />
                      <div className="absolute left-3 right-3 top-1/2 h-0.5 bg-white/90 animate-pulse" />
                    </div>
                  </div>
                  <div className="absolute bottom-4 left-0 right-0 px-4">
                    <p className="text-center text-white text-sm font-medium drop-shadow-lg">
                      Align QR code inside the frame
                    </p>
                    <p className="text-center text-white/80 text-xs mt-1">
                      Keep your phone steady and avoid glare
                    </p>
                  </div>
                </>
              )}
            </div>

            {!isScanning && (
              <div className="bg-white rounded-2xl shadow-soft border border-gray-100 overflow-hidden animate-slide-up">
                <div className="p-6">
                  <div className="bg-gray-50 rounded-xl p-4 flex justify-center mb-5">
                    {image && (
                      <img
                        src={image}
                        alt="QR Code"
                        className="w-48 h-48 object-contain"
                      />
                    )}
                  </div>
                  <div className="space-y-4">
                    <div>
                      <p className="text-xs text-gray-500 mb-1">
                        {isWalletAddressCollection ? 'Wallet address' : 'VPA Detected'}
                      </p>
                      <p className="text-sm font-medium text-gray-900 break-all">
                        {scannedDetails.vpa || 'Not found in this QR'}
                      </p>
                    </div>
                    {!isWalletAddressCollection && (
                      <div>
                        <p className="text-xs text-gray-500 mb-1">Merchant</p>
                        <p className="text-sm font-medium text-gray-900 break-all">
                          {scannedDetails.merchantName || 'Not available'}
                        </p>
                      </div>
                    )}
                    <div>
                      <p className="text-xs text-gray-500 mb-1">Provider</p>
                      <p className="text-sm font-medium text-gray-900 break-all">
                        {scannedDetails.providerName || 'Not available'}
                      </p>
                    </div>
                    <div className="flex gap-3">
                      <button
                        type="button"
                        onClick={handleReset}
                        className="flex-1 py-2.5 border border-gray-200 rounded-xl font-medium text-gray-700 hover:bg-gray-50 transition-colors"
                        disabled={isSaving}
                      >
                        Scan Again
                      </button>
                      <button
                        type="button"
                        onClick={handleScan}
                        disabled={!scannedDetails.vpa || isSaving}
                        className="flex-1 py-2.5 bg-brand text-white rounded-xl hover:bg-brand-dark active:scale-[0.98] font-semibold transition-all shadow-soft disabled:opacity-50 disabled:cursor-not-allowed disabled:active:scale-100"
                      >
                        {isSaving
                          ? 'Adding...'
                          : isWalletAddressCollection
                            ? 'Add wallet'
                            : 'Add VPA'}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default QRScanner;
